import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Music, Star, Cross, Play, Youtube, Clock, Eye, Calendar, Users, ThumbsUp, ExternalLink, RefreshCw } from 'lucide-react';
import Navigation from './Navigation';
import Footer from './Footer';
import SEOHead from './SEOHead';

const EpisoadePage = () => {
  const [youtubeEpisodes, setYoutubeEpisodes] = useState([]);
  const [channelInfo, setChannelInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchEpisodes();
  }, []);

  const fetchEpisodes = async () => {
    try {
      setError(null);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/youtube/episodes?max_results=20`);
      
      if (response.ok) {
        const data = await response.json();
        setYoutubeEpisodes(data.episodes || []);
        setChannelInfo(data.channel_info || null);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Eroare la încărcarea episoadelor');
      }
    } catch (error) {
      console.error('Error fetching episodes:', error);
      setError('Eroare de conexiune. Te rugăm să încerci din nou mai târziu.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Clear cache first
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/youtube/cache/clear`, {
        method: 'POST'
      });
      // Then fetch new data
      await fetchEpisodes();
    } catch (error) {
      console.error('Error refreshing:', error);
      setRefreshing(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ro-RO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatViewCount = (count) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Ieri';
    if (diffDays < 7) return `${diffDays} zile în urmă`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} săptămâni în urmă`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} luni în urmă`;
    return `${Math.floor(diffDays / 365)} ani în urmă`;
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "PodcastSeries",
    "name": "Cuvânt și Rugăciune",
    "description": "Podcast creștin cu studii biblice profunde și rugăciune contemplativă prezentate de Mircea Stanciu",
    "url": "https://cuvantsirugaciune.ro/episoade",
    "author": {
      "@type": "Person",
      "name": "Mircea Stanciu"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Cuvânt și Rugăciune",
      "url": "https://cuvantsirugaciune.ro"
    },
    "genre": "Religion & Spirituality",
    "inLanguage": "ro",
    "associatedMedia": {
      "@type": "VideoObject",
      "contentUrl": "https://www.youtube.com/@mircea_stanciu",
      "embedUrl": "https://www.youtube.com/@mircea_stanciu"
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="bg-slate-900 text-white overflow-hidden">
      <SEOHead 
        title="Episoade Podcast Cuvânt și Rugăciune - Studii Biblice cu Mircea Stanciu"
        description="Ascultă episoadele podcast-ului Cuvânt și Rugăciune cu Mircea Stanciu. Studii biblice profunde, meditații spirituale și învățături creștine pentru sufletul tău."
        keywords="episoade podcast crestin, mircea stanciu episoade, studii biblice audio, podcast biblic roman, meditatii crestine, invatatura biblica"
        canonicalUrl="/episoade"
        ogTitle="Episoade Podcast Creștin - Cuvânt și Rugăciune cu Mircea Stanciu"
        ogDescription="Descoperă episoadele pline de învățătură biblică și înțelepciune spirituală. Podcast creștin românesc cu studii biblice profunde."
        structuredData={structuredData}
      />
      
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.7), rgba(15, 23, 42, 0.8)), url('https://images.unsplash.com/photo-1617610882105-71c5d7447837')`
          }}
        />
        
        {/* Decorative cross pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="grid grid-cols-6 sm:grid-cols-8 gap-8 p-8">
            {[...Array(48)].map((_, i) => (
              <Cross key={i} className="w-4 h-4 text-amber-400" />
            ))}
          </div>
        </div>
        
        <div className="relative container mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="max-w-4xl mx-auto"
          >
            <div className="flex justify-center items-center space-x-4 mb-6">
              <Music className="w-8 h-8 sm:w-12 sm:h-12 text-amber-400" />
              <Cross className="w-10 h-10 sm:w-16 sm:h-16 text-amber-400" />
              <Music className="w-8 h-8 sm:w-12 sm:h-12 text-amber-400" />
            </div>
            
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500 bg-clip-text text-transparent leading-tight">
              Episoade Podcast
            </h1>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-lg sm:text-xl md:text-2xl text-slate-300 mb-3 sm:mb-4 leading-relaxed px-4"
            >
              Descoperă cele mai noi episoade pline de învățătură biblică și înțelepciune spirituală
            </motion.p>
            
            {channelInfo && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.8 }}
                className="flex flex-col sm:flex-row gap-4 justify-center items-center text-sm sm:text-base text-slate-400 mb-6"
              >
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4" />
                  <span>{formatViewCount(channelInfo.subscriber_count)} abonați</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Play className="w-4 h-4" />
                  <span>{channelInfo.video_count} videouri</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Eye className="w-4 h-4" />
                  <span>{formatViewCount(channelInfo.view_count)} vizualizări</span>
                </div>
              </motion.div>
            )}
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center px-4"
            >
              {youtubeEpisodes.length > 0 && (
                <button 
                  onClick={() => window.open(youtubeEpisodes[0].url, '_blank')} 
                  className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl flex items-center justify-center space-x-2"
                >
                  <Play className="w-5 h-5" />
                  <span>Ascultă Ultimul Episod</span>
                </button>
              )}
              
              <button onClick={() => window.open('https://www.youtube.com/@mircea_stanciu', '_blank')} className="w-full sm:w-auto border-2 border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-slate-900 font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2">
                <Youtube className="w-5 h-5" />
                <span>Vezi pe YouTube</span>
              </button>

              <button 
                onClick={handleRefresh}
                disabled={refreshing}
                className="w-full sm:w-auto border-2 border-slate-600 text-slate-300 hover:bg-slate-600 hover:text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Se actualizează...' : 'Actualizează'}</span>
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Episodes Section */}
      <section className="py-16 sm:py-20 relative">
        <div className="container mx-auto px-4 sm:px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <div className="flex justify-center items-center space-x-3 mb-4">
              <Music className="w-8 h-8 text-amber-400" />
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-amber-400">
                {loading ? 'Se încarcă episoadele...' : 
                 error ? 'Episoade Temporar Indisponibile' : 
                 `${youtubeEpisodes.length} Episoade Disponibile`}
              </h2>
              <Music className="w-8 h-8 text-amber-400" />
            </div>
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto px-4">
              {error ? 'Ne pare rău, episoadele nu pot fi încărcate în acest moment.' :
               'Fiecare episod este o călătorie spirituală prin Cuvântul lui Dumnezeu'}
            </p>
          </motion.div>
          
          {loading ? (
            <div className="text-center">
              <div className="inline-flex items-center space-x-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400"></div>
                <span className="text-slate-300 text-lg">Se încarcă episoadele din YouTube...</span>
              </div>
            </div>
          ) : error ? (
            <div className="text-center">
              <div className="bg-slate-800/50 rounded-3xl p-8 sm:p-12 border border-red-400/20 max-w-2xl mx-auto">
                <Cross className="w-16 h-16 text-red-400 mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-red-400 mb-4">Episoade Temporar Indisponibile</h3>
                <p className="text-lg text-slate-300 mb-6">{error}</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button 
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 font-bold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                    <span>{refreshing ? 'Se actualizează...' : 'Încearcă Din Nou'}</span>
                  </button>
                  <button 
                    onClick={() => window.open('https://www.youtube.com/@mircea_stanciu', '_blank')}
                    className="border-2 border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-slate-900 font-bold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2"
                  >
                    <Youtube className="w-5 h-5" />
                    <span>Vezi pe YouTube</span>
                  </button>
                </div>
              </div>
            </div>
          ) : youtubeEpisodes.length > 0 ? (
            <motion.div
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
            >
              {youtubeEpisodes.map((episode, index) => (
                <motion.div
                  key={episode.id}
                  variants={fadeInUp}
                  className="bg-slate-800/70 rounded-3xl overflow-hidden border border-amber-400/20 hover:border-amber-400/40 transition-all duration-300 group hover:transform hover:scale-105 cursor-pointer"
                  onClick={() => window.open(episode.url, '_blank')}
                >
                  <div className="relative h-48 sm:h-56 overflow-hidden">
                    <img 
                      src={episode.thumbnail_url} 
                      alt={episode.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                    
                    {/* Episode number */}
                    <div className="absolute top-3 left-3 bg-amber-400 text-slate-900 px-3 py-1 rounded-lg text-sm font-bold">
                      Episod {index + 1}
                    </div>
                    
                    {/* Duration */}
                    <div className="absolute top-3 right-3 bg-black/80 text-white px-2 py-1 rounded-lg text-sm flex items-center space-x-1">
                      <Clock className="w-3 h-3" />
                      <span>{episode.duration}</span>
                    </div>
                    
                    {/* Play button */}
                    <div className="absolute bottom-3 right-3">
                      <div className="bg-amber-400 hover:bg-amber-500 text-slate-900 rounded-full p-3 transition-colors group-hover:scale-110 transform duration-300">
                        <Play className="w-6 h-6" />
                      </div>
                    </div>
                    
                    {/* Views and likes */}
                    <div className="absolute bottom-3 left-3 flex items-center space-x-3 text-white text-sm">
                      <div className="flex items-center space-x-1 bg-black/60 px-2 py-1 rounded">
                        <Eye className="w-3 h-3" />
                        <span>{formatViewCount(episode.view_count)}</span>
                      </div>
                      {episode.like_count > 0 && (
                        <div className="flex items-center space-x-1 bg-black/60 px-2 py-1 rounded">
                          <ThumbsUp className="w-3 h-3" />
                          <span>{formatViewCount(episode.like_count)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="p-4 sm:p-6">
                    <h3 className="text-lg sm:text-xl font-semibold text-amber-400 mb-3 line-clamp-2 group-hover:text-amber-300 transition-colors">
                      {episode.title}
                    </h3>
                    <p className="text-sm sm:text-base text-slate-300 mb-4 line-clamp-3">
                      {episode.description || 'Alătură-te acestei călătorii spirituale prin Cuvântul lui Dumnezeu. Un episod plin de învățătură biblică și înțelepciune spirituală pentru sufletul tău.'}
                    </p>
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(episode.published_at)}</span>
                      </div>
                      <span className="text-amber-400">{getRelativeTime(episode.published_at)}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            // Fallback content if no episodes
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-slate-800/50 rounded-3xl p-8 sm:p-12 border border-amber-400/20 max-w-4xl mx-auto">
                <Music className="w-16 h-16 text-amber-400 mx-auto mb-6" />
                <h3 className="text-2xl sm:text-3xl font-bold text-amber-400 mb-4">Episoade în Curând</h3>
                <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                  Lucrăm cu dragoste la conținut nou plin de învățătură biblică. 
                  Abonează-te pe YouTube pentru a fi primul care primește notificări despre noile episoade.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button 
                    onClick={() => window.open('https://www.youtube.com/@mircea_stanciu', '_blank')}
                    className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 font-bold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2"
                  >
                    <Youtube className="w-5 h-5" />
                    <span>Abonează-te pe YouTube</span>
                  </button>
                  <button 
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="border-2 border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-slate-900 font-bold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                    <span>Verifică Din Nou</span>
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default EpisoadePage;